import React, { useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { Dismiss16Regular, ArrowDownload16Regular, CheckmarkCircle20Filled } from "@fluentui/react-icons";
import { chatService } from "../../services/chatService";
import VorgangPickerAuswahl from "../vorgang/picker/VorgangPickerAuswahl";
import { INK, INK2, LINE, BG_SOFT, AUB } from "./chatTheme";
import "../calendar/calendar.css";

type Props = {
  attachmentId: string;
  fileName: string;
  onClose: () => void;
};

/**
 * Chat-Anhang weiterverwenden: zuerst einem **Vorgang zuweisen** (Ablage als
 * Dokument am Vorgang und beim Kunden des Vorgangs), danach — falls gewünscht —
 * auf den eigenen Rechner herunterladen. Genau die Reihenfolge, die der Chat
 * fachlich braucht: Dateien sollen in der Akte landen, nicht nur im Download-
 * Ordner.
 *
 * Die Auswahl nutzt `VorgangPickerAuswahl` (serverseitige Suche + Rangfolge
 * Kontakt → eigene/Team → alle); die Optik kommt aus `calendar.css` (.cal-vgd-*).
 */
export const AttachmentAssignDialog: React.FC<Props> = ({ attachmentId, fileName, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<{ titel: string; doppelt: boolean } | null>(null);

  const zuweisen = async (v: { id: number; titel: string }) => {
    setBusy(true);
    setFehler(null);
    try {
      const res = await chatService.assignAttachmentToVorgang(attachmentId, v.id);
      setErfolg({ titel: v.titel, doppelt: res.status === "duplicate" });
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Zuweisen fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Anhang einem Vorgang zuweisen"
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        // Über dem Fenster-Layer (WindowLayer 9000).
        zIndex: 100000,
        background: "rgba(20,8,20,0.34)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        className="kvp-rd cal-rd"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(520px, 100%)",
          maxHeight: "min(700px, 88vh)",
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          border: `1px solid ${LINE}`,
          borderRadius: 14,
          boxShadow: "0 24px 64px rgba(20,8,20,0.28)",
          fontFamily: "'Barlow', 'Segoe UI', sans-serif",
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: `1px solid ${LINE}` }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 800, color: INK, fontSize: 16, fontFamily: "'Playfair Display', Georgia, serif" }}>
              Datei zuweisen
            </div>
            <div style={{ color: INK2, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {fileName}
            </div>
          </div>
          <button type="button" aria-label="Schließen" onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", color: INK2, display: "inline-flex" }}>
            <Dismiss16Regular />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding: 12, gap: 8 }}>
          {fehler && (
            <div role="alert" style={{ padding: "6px 10px", borderRadius: 8, background: "#fdf0f0", color: "#8a2626", fontSize: 12.5 }}>
              {fehler}
            </div>
          )}

          {erfolg ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "8px 4px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, color: INK, fontSize: 14 }}>
                <span style={{ color: "#2f7d4f", display: "inline-flex" }}>
                  <CheckmarkCircle20Filled />
                </span>
                <span>
                  {erfolg.doppelt ? "Liegt bereits am Vorgang " : "Zugewiesen an "}
                  <strong>{erfolg.titel}</strong>
                </span>
              </div>
              <div style={{ color: INK2, fontSize: 12.5 }}>
                Die Datei ist als Dokument am Vorgang abgelegt (Kategorie „Chat-Anhang").
              </div>
            </div>
          ) : (
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              <div style={{ color: AUB, fontSize: 12.5, fontWeight: 700, padding: "0 2px 8px" }}>
                Vorgang wählen
              </div>
              <VorgangPickerAuswahl onPick={(v) => void zuweisen(v)} disabled={busy} />
            </div>
          )}
        </div>

        <div style={{ borderTop: `1px solid ${LINE}`, padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, background: BG_SOFT }}>
          <a
            href={chatService.attachmentDownloadUrl(attachmentId)}
            target="_blank"
            rel="noreferrer"
            download={fileName}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, border: `1px solid ${LINE}`, background: "#fff", borderRadius: 10, padding: "7px 12px", fontSize: 13, textDecoration: "none", color: INK }}
          >
            <ArrowDownload16Regular />
            Auf den PC herunterladen
          </a>
          <button type="button" onClick={onClose} style={{ border: `1px solid ${LINE}`, background: "#fff", borderRadius: 10, padding: "7px 12px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", color: INK }}>
            {erfolg ? "Fertig" : "Abbrechen"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AttachmentAssignDialog;
